// =============================================================================
// useMarketLayouts — fetch + auto-warm /feed/{slug}/match/{eventId}/market-layouts
// =============================================================================
// #409 Phase 4-5. On kamp-side mount:
//
//   1. GET /market-layouts to see what's already cached.
//   2. If `pending` is non-empty, POST /market-layouts/warm with the
//      first 3 pending marketIds. Engine composes them in background
//      (semaphore=2, budget-guarded).
//   3. Poll GET every POLL_INTERVAL_MS for up to POLL_DURATION_MS, stop
//      when the requested set lands OR timer exhausts OR unmount.
//
// Phase 5 additions:
//
//   4. Hook exposes triggerTailWarm(marketId). Render's MarketSection
//      uses IntersectionObserver to call this once when a tail section
//      (#4-5) scrolls into view. Idempotent server-side via Redis
//      SETNX dedup, so multiple calls for the same id are safe.
//   5. Each tail-warm restarts a short polling cycle (8s + 15s pings)
//      so the new compose is visible without a page reload.
//
// Cost discipline:
//   - Top-3 always; tail only on user scroll. Bounded by
//     POPULAR_MARKETS_LIMIT (=5) and per-tenant daily budget guard.
//   - /warm is idempotent — duplicate calls cost nothing.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MarketLayoutsResponse } from '../api/createEngineClient.js';
import { useEngineClient } from '../api/EngineClientProvider.js';
import { reportError } from '../observability.js';

interface State {
  data: MarketLayoutsResponse | null;
  loading: boolean;
  error: Error | null;
  /**
   * #409 Phase 5 — trigger a /warm for a tail marketId that just scrolled
   * into view. Server-side dedup means multiple calls for the same id
   * are safe (no extra spend). The hook auto-refreshes a couple of
   * seconds later to pick up the new compose without a page reload.
   */
  triggerTailWarm: (marketId: string) => void;
}

const POLL_INTERVAL_MS = 3_000;           // every 3s during initial cycle
const POLL_DURATION_MS = 30_000;          // up to 30s total
const WARM_TOP_N = 3;                     // auto-fire composes for top-3 only
const TAIL_REFRESH_DELAYS_MS = [8_000, 16_000]; // when to re-GET after tail warm

/**
 * Module-level inflight dedup (audit fix #469). Both initial-warm and
 * tail-warm use raw `fetch()` instead of the engine client (which has
 * its own dedup) — that left a gap where React strict-mode double-mount
 * or rapid remount could fire duplicate POSTs for the same
 * (slug, eventId, marketId) tuple. Server-side Redis SETNX absorbs the
 * duplicates safely, but the client still pays bandwidth + log noise.
 *
 * Keyed on the FULL request signature (URL + serialised marketIds) so
 * a tail-warm for "btts" and an initial-warm for ["match1x2","btts"]
 * are correctly treated as distinct in-flight requests.
 *
 * Promise is removed on settle so a follow-up warm (e.g. after a
 * cache invalidation) actually fires fresh.
 */
const warmInflight = new Map<string, Promise<void>>();

function warmDedupKey(url: string, marketIds: readonly string[]): string {
  return `${url}::${[...marketIds].sort().join(',')}`;
}

async function dedupedWarmFetch(
  url: string,
  marketIds: readonly string[],
  signal: AbortSignal,
): Promise<void> {
  const key = warmDedupKey(url, marketIds);
  const existing = warmInflight.get(key);
  if (existing) return existing;

  const promise = fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ marketIds }),
    signal,
    credentials: 'same-origin',
  })
    .then((res) => {
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[useMarketLayouts] /warm returned ${res.status}`);
      }
    })
    .finally(() => {
      // Always clear on settle so a follow-up warm (post-cache-bust)
      // actually fires rather than returning the resolved old promise.
      if (warmInflight.get(key) === promise) {
        warmInflight.delete(key);
      }
    });

  warmInflight.set(key, promise);
  return promise;
}

export function useMarketLayouts(eventId: string): State {
  const client = useEngineClient();
  const slug = client.slug;
  const [data, setData] = useState<MarketLayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Per-mount AbortController + dedup set for tail warms. Re-created on
  // every effect run (i.e. every eventId change or remount).
  const ctrlRef = useRef<AbortController | null>(null);
  const tailWarmedRef = useRef<Set<string>>(new Set());

  const base = `/onsite/feed/${encodeURIComponent(slug)}/match/${encodeURIComponent(eventId)}`;
  const getUrl = `${base}/market-layouts`;
  const warmUrl = `${base}/market-layouts/warm`;

  // -------------------------------------------------------------------------
  // Lazy refresh after tail-warm. Defined as a callback so MarketSection
  // (via IntersectionObserver) can call it without re-creating the hook.
  // -------------------------------------------------------------------------
  const triggerTailWarm = useCallback(
    (marketId: string): void => {
      const ctrl = ctrlRef.current;
      if (!ctrl || ctrl.signal.aborted) return;
      if (tailWarmedRef.current.has(marketId)) return;
      tailWarmedRef.current.add(marketId);

      // eslint-disable-next-line no-console
      console.info('[useMarketLayouts] /warm (tail)', marketId);

      // #469 — dedupedWarmFetch coalesces duplicate POSTs across React
      // strict-mode remounts and sibling component instances. Errors
      // are non-fatal — render keeps the skeleton until the polling
      // GET below picks up the cache hit.
      void dedupedWarmFetch(warmUrl, [marketId], ctrl.signal).catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[useMarketLayouts] /warm tail failed:', err);
      });

      // Schedule a couple of refreshes so the new compose lands visibly.
      // We don't poll indefinitely — two attempts at 8s and 16s catch
      // typical Haiku latency (12-15s on cold cache).
      for (const delay of TAIL_REFRESH_DELAYS_MS) {
        setTimeout((): void => {
          if (ctrl.signal.aborted) return;
          void refetchOnce(ctrl, getUrl).then((next) => {
            if (next && !ctrl.signal.aborted) setData(next);
          });
        }, delay);
      }
    },
    [warmUrl, getUrl],
  );

  // -------------------------------------------------------------------------
  // Initial fetch + auto-warm + poll cycle for top-N markets.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    tailWarmedRef.current = new Set();
    setLoading(true);
    setError(null);

    /** Fire-and-forget /warm — engine returns 202, we don't wait.
     *  Uses module-level dedup (audit fix #469). */
    function triggerWarm(marketIds: string[]): void {
      if (marketIds.length === 0) return;
      // eslint-disable-next-line no-console
      console.info('[useMarketLayouts] /warm', marketIds);
      void dedupedWarmFetch(warmUrl, marketIds, ctrl.signal).catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[useMarketLayouts] /warm failed:', err);
      });
    }

    // Fire-and-forget by design: errors land in state inside the IIFE.
    void (async (): Promise<void> => {
      try {
        // 1. Initial GET.
        const initial = await refetchOnce(ctrl, getUrl);
        if (ctrl.signal.aborted || initial == null) return;
        setData(initial);
        setLoading(false);

        // 2. /warm top-3 if any are pending.
        // Design audit #478 — pending now ships as {marketId, label, …}
        // objects. /warm endpoint expects marketId strings, so we project
        // here and downstream code keeps the same string[] contract.
        const topPendingIds = initial.pending.slice(0, WARM_TOP_N).map((p) => p.marketId);
        if (topPendingIds.length === 0) {
          // eslint-disable-next-line no-console
          console.info('[useMarketLayouts] all top-N cached, no warm needed');
          return;
        }
        triggerWarm(topPendingIds);

        // 3. Poll until top-3 land or timer exhausts.
        const deadline = Date.now() + POLL_DURATION_MS;
        while (Date.now() < deadline && !ctrl.signal.aborted) {
          await sleep(POLL_INTERVAL_MS, ctrl.signal);
          if (ctrl.signal.aborted) return;
          const next = await refetchOnce(ctrl, getUrl);
          if (next == null) return;
          setData(next);
          const stillPending = next.pending.filter((p) => topPendingIds.includes(p.marketId));
          if (stillPending.length === 0) {
            // eslint-disable-next-line no-console
            console.info('[useMarketLayouts] poll complete, top-N landed');
            return;
          }
        }
      } catch (err: unknown) {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const e = err instanceof Error ? err : new Error(String(err));
        // eslint-disable-next-line no-console
        console.warn(`[useMarketLayouts] failed — ${e.name}: ${e.message}`);
        reportError(e, { hook: 'useMarketLayouts', eventId });
        setError(e);
        setLoading(false);
      }
    })();

    return (): void => {
      ctrl.abort();
      ctrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  return { data, loading, error, triggerTailWarm };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Single GET; returns null on abort or hard failure. */
async function refetchOnce(
  ctrl: AbortController,
  url: string,
): Promise<MarketLayoutsResponse | null> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: ctrl.signal,
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`GET ${res.status}`);
    return (await res.json()) as MarketLayoutsResponse;
  } catch (err) {
    if (ctrl.signal.aborted) return null;
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    throw err;
  }
}

/** Promise-based sleep that rejects on abort. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
